import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Card,
  Typography,
  Button,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  CircularProgress,
  Tooltip,
  LinearProgress,
} from '@mui/material';
import {
  CloudUpload,
  Download,
  Delete,
  Refresh,
  AutoFixHigh,
  Description,
} from '@mui/icons-material';
import { knowledgeBaseAPI } from '../../services/api';
import useIsMobile from '../../hooks/useIsMobile';

const LANGUAGES = [
  { code: 'en', label: 'English (en)' },
  { code: 'mr', label: 'मराठी (mr)' },
];

function formatFileSize(bytes) {
  if (bytes === null || bytes === undefined) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

function formatDate(isoString) {
  if (!isoString) return '—';
  return new Date(isoString).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

function StatusChip({ status }) {
  const config = {
    pending:    { label: 'Pending',    color: 'default', bgcolor: '#F3F4F6', textColor: '#6B7280' },
    processing: { label: 'Processing', color: 'warning', bgcolor: '#FFFBEB', textColor: '#D97706' },
    completed:  { label: 'Completed',  color: 'success', bgcolor: '#ECFDF5', textColor: '#059669' },
    failed:     { label: 'Failed',     color: 'error',   bgcolor: '#FEF2F2', textColor: '#DC2626' },
  };
  const cfg = config[status] || config.pending;
  return (
    <Chip
      label={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          {status === 'processing' && (
            <CircularProgress size={10} thickness={5} sx={{ color: cfg.textColor }} />
          )}
          <span>{cfg.label}</span>
        </Box>
      }
      size="small"
      sx={{
        bgcolor: cfg.bgcolor,
        color: cfg.textColor,
        fontWeight: 500,
        fontSize: '0.825rem',
        '& .MuiChip-label': { display: 'flex', alignItems: 'center' },
      }}
    />
  );
}

function KnowledgeBase() {
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState(0);
  const [filesByLang, setFilesByLang] = useState({ en: [], mr: [] });
  const [loading, setLoading] = useState(false);

  // Upload dialog
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  // Delete confirm dialog
  const [deleteTarget, setDeleteTarget] = useState(null); // { id, filename }
  const [deleting, setDeleting] = useState(false);

  // Rebuild confirm dialog
  const [rebuildLangTarget, setRebuildLangTarget] = useState(null); // 'en' or 'mr'
  const [rebuilding, setRebuilding] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  // Current language
  const currentLang = LANGUAGES[activeTab].code;

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchFiles = useCallback(async (lang) => {
    setLoading(true);
    try {
      const res = await knowledgeBaseAPI.getFiles(lang);
      setFilesByLang(prev => ({ ...prev, [lang]: res.data.files || [] }));
    } catch (err) {
      showSnackbar(`Failed to load files for ${lang}: ${err.response?.data?.detail || err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load for both languages
  useEffect(() => {
    fetchFiles('en');
    fetchFiles('mr');
  }, [fetchFiles]);

  // Auto-refresh every 10s when any file is in 'processing' status
  useEffect(() => {
    const allFiles = [...(filesByLang.en || []), ...(filesByLang.mr || [])];
    const hasProcessing = allFiles.some(f => f.embedding_status === 'processing');
    if (!hasProcessing) return;

    const timer = setInterval(() => {
      fetchFiles('en');
      fetchFiles('mr');
    }, 10000);
    return () => clearInterval(timer);
  }, [filesByLang, fetchFiles]);

  const handleTabChange = (_, newValue) => {
    setActiveTab(newValue);
  };

  // ── Upload ──────────────────────────────────────────────────────────────────

  const handleUploadOpen = () => {
    setUploadFile(null);
    setUploadOpen(true);
  };

  const handleUploadClose = () => {
    setUploadOpen(false);
    setUploadFile(null);
  };

  const ALLOWED_EXTENSIONS = ['.txt', '.pdf', '.docx'];

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        showSnackbar('Only .txt, .pdf, and .docx files are accepted', 'error');
        return;
      }
    }
    setUploadFile(file || null);
  };

  const handleUploadSubmit = async () => {
    if (!uploadFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('language', currentLang);
      await knowledgeBaseAPI.uploadFile(formData);
      showSnackbar(`File "${uploadFile.name}" uploaded successfully`);
      handleUploadClose();
      await fetchFiles(currentLang);
    } catch (err) {
      showSnackbar(`Upload failed: ${err.response?.data?.detail || err.message}`, 'error');
    } finally {
      setUploading(false);
    }
  };

  // ── Download ────────────────────────────────────────────────────────────────

  const handleDownload = async (fileId, filename) => {
    try {
      const res = await knowledgeBaseAPI.downloadFile(fileId);
      const url = res.data.download_url;
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      showSnackbar(`Download failed: ${err.response?.data?.detail || err.message}`, 'error');
    }
  };

  // ── Delete ──────────────────────────────────────────────────────────────────

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await knowledgeBaseAPI.deleteFile(deleteTarget.id);
      showSnackbar(`File "${deleteTarget.filename}" deleted successfully`);
      setDeleteTarget(null);
      await fetchFiles(currentLang);
    } catch (err) {
      showSnackbar(`Delete failed: ${err.response?.data?.detail || err.message}`, 'error');
    } finally {
      setDeleting(false);
    }
  };

  // ── Re-embed ────────────────────────────────────────────────────────────────

  const handleReembed = async (fileId, filename) => {
    try {
      await knowledgeBaseAPI.reembedFile(fileId);
      showSnackbar(`Re-embed triggered for "${filename}"`);
      await fetchFiles(currentLang);
    } catch (err) {
      showSnackbar(`Re-embed failed: ${err.response?.data?.detail || err.message}`, 'error');
    }
  };

  // ── Rebuild All ─────────────────────────────────────────────────────────────

  const handleRebuildConfirm = async () => {
    if (!rebuildLangTarget) return;
    setRebuilding(true);
    try {
      await knowledgeBaseAPI.rebuildAll(rebuildLangTarget);
      showSnackbar(`Rebuild triggered for all ${rebuildLangTarget.toUpperCase()} files`);
      setRebuildLangTarget(null);
      await fetchFiles(rebuildLangTarget);
    } catch (err) {
      showSnackbar(`Rebuild failed: ${err.response?.data?.detail || err.message}`, 'error');
    } finally {
      setRebuilding(false);
    }
  };

  // ── Stats helpers ───────────────────────────────────────────────────────────

  const files = filesByLang[currentLang] || [];
  const completedCount = files.filter(f => f.embedding_status === 'completed').length;
  const processingCount = files.filter(f => f.embedding_status === 'processing').length;
  const pendingCount = files.filter(f => f.embedding_status === 'pending').length;
  const failedCount = files.filter(f => f.embedding_status === 'failed').length;

  const tabBadge = (lang) => {
    const count = (filesByLang[lang] || []).length;
    return count > 0 ? ` (${count})` : '';
  };

  return (
    <Box>
      {/* Language Tabs */}
      <Card sx={{ mb: 2.5, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: '1px solid', borderColor: 'grey.200', px: 2, bgcolor: 'grey.50' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              '& .MuiTab-root': { fontWeight: 500, fontSize: '0.963rem', minHeight: 48 },
              '& .Mui-selected': { color: 'primary.main' },
              '& .MuiTabs-indicator': { bgcolor: 'primary.main' },
            }}
          >
            {LANGUAGES.map((lang, idx) => (
              <Tab key={lang.code} label={`${lang.label}${tabBadge(lang.code)}`} id={`kb-tab-${idx}`} />
            ))}
          </Tabs>
        </Box>

        {/* Stats row */}
        <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Description sx={{ color: 'grey.400', fontSize: 22 }} />
            <Typography variant="body2" sx={{ color: 'grey.600' }}>
              <strong>{files.length}</strong> files
            </Typography>
          </Box>
          {completedCount > 0 && (
            <Chip
              label={`${completedCount} completed`}
              size="small"
              sx={{ bgcolor: '#ECFDF5', color: '#059669', fontWeight: 500, fontSize: '0.825rem' }}
            />
          )}
          {processingCount > 0 && (
            <Chip
              label={`${processingCount} processing`}
              size="small"
              icon={<CircularProgress size={10} thickness={5} sx={{ color: '#D97706 !important' }} />}
              sx={{ bgcolor: '#FFFBEB', color: '#D97706', fontWeight: 500, fontSize: '0.825rem' }}
            />
          )}
          {pendingCount > 0 && (
            <Chip
              label={`${pendingCount} pending`}
              size="small"
              sx={{ bgcolor: '#F3F4F6', color: '#6B7280', fontWeight: 500, fontSize: '0.825rem' }}
            />
          )}
          {failedCount > 0 && (
            <Chip
              label={`${failedCount} failed`}
              size="small"
              sx={{ bgcolor: '#FEF2F2', color: '#DC2626', fontWeight: 500, fontSize: '0.825rem' }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            startIcon={rebuilding ? <CircularProgress size={14} /> : <AutoFixHigh />}
            onClick={() => setRebuildLangTarget(currentLang)}
            disabled={rebuilding || files.length === 0}
            size="small"
            sx={{ borderColor: 'grey.300', color: 'grey.700', '&:hover': { borderColor: 'primary.main', color: 'primary.main' } }}
          >
            Rebuild All Embeddings
          </Button>
          <Button
            variant="contained"
            startIcon={<CloudUpload />}
            onClick={handleUploadOpen}
            size="small"
            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            Upload File
          </Button>
        </Box>
      </Card>

      {/* Files Table */}
      <Card>
        {loading && <LinearProgress sx={{ borderRadius: '8px 8px 0 0' }} />}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.50' }}>
                <TableCell sx={{ fontWeight: 600, color: 'grey.700', fontSize: '0.894rem', py: 1.5 }}>Filename</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'grey.700', fontSize: '0.894rem', py: 1.5 }}>Size</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'grey.700', fontSize: '0.894rem', py: 1.5 }}>Uploaded</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'grey.700', fontSize: '0.894rem', py: 1.5 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600, color: 'grey.700', fontSize: '0.894rem', py: 1.5 }} align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {files.length === 0 && !loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <Description sx={{ fontSize: 42, color: 'grey.300', mb: 1, display: 'block', mx: 'auto' }} />
                    <Typography variant="body2" sx={{ color: 'grey.500' }}>
                      No files found for {LANGUAGES[activeTab].label}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'grey.400' }}>
                      Upload a .txt, .pdf, or .docx file to get started
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                files.map((file) => (
                  <TableRow
                    key={file.id}
                    hover
                    sx={{ '&:last-child td': { border: 0 } }}
                  >
                    <TableCell sx={{ py: 1.25 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Description sx={{ fontSize: 18, color: 'grey.400', flexShrink: 0 }} />
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 500, color: 'grey.800', fontSize: '0.894rem' }}>
                            {file.filename}
                          </Typography>
                          {file.original_filename !== file.filename && (
                            <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.77rem' }}>
                              Original: {file.original_filename}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.25 }}>
                      <Typography variant="body2" sx={{ color: 'grey.600', fontSize: '0.894rem' }}>
                        {formatFileSize(file.file_size)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ py: 1.25 }}>
                      <Typography variant="body2" sx={{ color: 'grey.600', fontSize: '0.894rem' }}>
                        {formatDate(file.uploaded_at)}
                      </Typography>
                      {file.uploaded_by && (
                        <Typography variant="caption" sx={{ color: 'grey.400', fontSize: '0.77rem' }}>
                          by {file.uploaded_by}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ py: 1.25 }}>
                      <StatusChip status={file.embedding_status} />
                    </TableCell>
                    <TableCell sx={{ py: 1.25 }} align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                        <Tooltip title="Download">
                          <IconButton
                            size="small"
                            onClick={() => handleDownload(file.id, file.filename)}
                            sx={{ color: 'grey.500', '&:hover': { color: 'primary.main', bgcolor: 'primary.lighter' } }}
                          >
                            <Download fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Re-embed">
                          <IconButton
                            size="small"
                            onClick={() => handleReembed(file.id, file.filename)}
                            disabled={file.embedding_status === 'processing'}
                            sx={{ color: 'grey.500', '&:hover': { color: 'warning.main', bgcolor: 'warning.lighter' } }}
                          >
                            <Refresh fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setDeleteTarget({ id: file.id, filename: file.filename })}
                            sx={{ color: 'grey.500', '&:hover': { color: 'error.main', bgcolor: 'error.lighter' } }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>

      {/* Upload Dialog */}
      <Dialog open={uploadOpen} onClose={handleUploadClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.1rem', pb: 1 }}>
          Upload Knowledge Base File
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <Alert severity="info" sx={{ mb: 2, fontSize: '0.894rem' }}>
            Uploading to <strong>{LANGUAGES[activeTab].label}</strong> language. Accepted formats: .txt, .pdf, .docx
          </Alert>
          <Box
            sx={{
              border: '2px dashed',
              borderColor: uploadFile ? 'primary.main' : 'grey.300',
              borderRadius: 2,
              p: 3,
              textAlign: 'center',
              bgcolor: uploadFile ? 'primary.lighter' : 'grey.50',
              cursor: 'pointer',
              transition: 'all 0.2s',
              '&:hover': { borderColor: 'primary.main', bgcolor: 'primary.lighter' },
            }}
            onClick={() => fileInputRef.current?.click()}
          >
            <CloudUpload sx={{ fontSize: 38, color: uploadFile ? 'primary.main' : 'grey.400', mb: 1 }} />
            {uploadFile ? (
              <Box>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'primary.main' }}>
                  {uploadFile.name}
                </Typography>
                <Typography variant="caption" sx={{ color: 'grey.500' }}>
                  {formatFileSize(uploadFile.size)}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography variant="body2" sx={{ color: 'grey.600', fontWeight: 500 }}>
                  Click to select a .txt, .pdf, or .docx file
                </Typography>
                <Typography variant="caption" sx={{ color: 'grey.400' }}>
                  or drag and drop here
                </Typography>
              </Box>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".txt,.pdf,.docx"
              style={{ display: 'none' }}
              onChange={handleFileSelect}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleUploadClose} disabled={uploading} sx={{ color: 'grey.600' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUploadSubmit}
            disabled={!uploadFile || uploading}
            startIcon={uploading ? <CircularProgress size={14} color="inherit" /> : <CloudUpload />}
            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirm Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
          Delete File
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'grey.700' }}>
            Are you sure you want to delete <strong>"{deleteTarget?.filename}"</strong>?
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.500', mt: 1, fontSize: '0.894rem' }}>
            This will remove the file from S3 and trigger a FAISS index rebuild.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting} sx={{ color: 'grey.600' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} color="inherit" /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rebuild All Confirm Dialog */}
      <Dialog open={!!rebuildLangTarget} onClose={() => !rebuilding && setRebuildLangTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
          Rebuild All Embeddings
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ color: 'grey.700' }}>
            Rebuild FAISS embeddings for all <strong>{rebuildLangTarget === 'en' ? 'English' : 'Marathi'}</strong> files?
          </Typography>
          <Typography variant="body2" sx={{ color: 'grey.500', mt: 1, fontSize: '0.894rem' }}>
            This will trigger the <code>refresh_faiss_index.sh --delete</code> script on the EC2 instance.
            All files will be marked as processing until the rebuild completes.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setRebuildLangTarget(null)} disabled={rebuilding} sx={{ color: 'grey.600' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleRebuildConfirm}
            disabled={rebuilding}
            startIcon={rebuilding ? <CircularProgress size={14} color="inherit" /> : <AutoFixHigh />}
            sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
          >
            {rebuilding ? 'Triggering...' : 'Rebuild All'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
          severity={snackbar.severity}
          sx={{ minWidth: 280 }}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default KnowledgeBase;
